import { useMemo } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Chip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ExternalServiceIcon from "../../../components/service/externalServiceIcon";
import CustomDataGrid from "../../../components/ui/customDataGrid/customDataGrid.component";
import { EXTERNAL_SERVICES_ENDPOINT } from "../../../consts";
import { useHasPermission } from "../../../contexts/auth";
import { ExternalServiceArea } from "../../../types/externalServices";
import { containsStringFilterOperators } from "../../../components/ui/customDataGrid/filterOperators";

export default function ExternalServicesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canView = useHasPermission("service.view_externalservice");

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: t("Name"),
        flex: 1,
        minWidth: 180,
        filterOperators: containsStringFilterOperators(),
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
            <Typography variant="body2" fontWeight={600}>
              {params.value}
            </Typography>
            {!params.row.is_active && <Chip label={t("Inactive")} size="small" />}
          </Box>
        ),
      },
      {
        field: "areas",
        headerName: t("Service area"),
        sortable: false,
        flex: 1.5,
        minWidth: 220,
        filterOperators: containsStringFilterOperators(),
        renderCell: (params) => {
          const areas: ExternalServiceArea[] = params.row.areas ?? [];
          if (areas.length === 0) return "-";
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", py: 0.75 }}>
              {areas.map((area) => (
                <Chip
                  key={`${area.city}|${area.county}|${area.region}`}
                  size="small"
                  variant="outlined"
                  label={`${area.city} · ${area.radius_km} km`}
                />
              ))}
            </Box>
          );
        },
      },
      {
        field: "phone_number",
        headerName: t("Phone Number"),
        filterOperators: containsStringFilterOperators(),
        valueGetter: (value) => value || "-",
      },
      {
        field: "email",
        headerName: t("Email"),
        minWidth: 180,
        filterOperators: containsStringFilterOperators(),
        valueGetter: (value) => value || "-",
      },
      {
        field: "open_jobs_count",
        headerName: t("Open jobs"),
        type: "number",
        filterable: false,
      },
      {
        field: "jobs_count",
        headerName: t("All jobs"),
        type: "number",
        filterable: false,
      },
    ],
    [t]
  );

  if (!canView) {
    return null;
  }

  return (
    <CustomDataGrid
      icon={<ExternalServiceIcon />}
      title={t("External services")}
      url={EXTERNAL_SERVICES_ENDPOINT}
      gridSettingsKey="EXTERNAL_SERVICES"
      columns={columns}
      dataGridProps={{ getRowHeight: () => "auto" }}
      refreshEventName={["externalServiceAdded", "externalServiceUpdated"]}
      onRowClick={(id) => navigate(`/service/external-services/${id}`)}
    />
  );
}
